import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Todo : famille "chose à faire".
 *
 * La liste des choses à faire de l'équipe, hors projet : qui fait quoi, pour
 * quand, avec quelle urgence. Les tâches d'un PROJET sont une autre famille,
 * `task` (rattachement `projectId`, ticket, ordre de travail) : une chose à
 * faire ne porte aucun lien vers un projet, et c'est ce qui les sépare
 * (`docs/socle/todo-base.md`).
 *
 * Le vocabulaire métier est porté par `type` (chaîne libre déclarée par
 * l'app) ; l'avancement (`status`) et l'urgence (`priority`) sont des chaînes
 * libres pilotées par l'app. Qui s'en charge, l'app le déclare en champ
 * personnalisé (une personne de l'annuaire).
 *
 * Accès : org-wide, même doctrine que task.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille todo (hors champs standard du socle). */
export const todoCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "tache"). */
  type: z.string().min(1),
  /** Avancement, chaîne libre pilotée par l'app (ex. "aFaire", "enCours", "termine"). Optionnel. */
  status: z.string().min(1).optional(),
  /** Urgence, chaîne libre pilotée par l'app (ex. "faible", "urgente"). Optionnel. */
  priority: z.string().min(1).optional(),
  /** Échéance. Optionnel. */
  dueAt: z.date().optional(),
} as const;

/** Schéma complet de la famille todo (champs standard + champs métier). */
export const TodoSchema = z.object({
  ...baseEntityFields,
  ...todoCoreFields,
});

/** Type d'une todo. */
export type Todo = z.infer<typeof TodoSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard sont remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const TodoCreateInputSchema = z.object({
  ...todoCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une todo. */
export type TodoCreateInput = z.infer<typeof TodoCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules).
 */
export const TodoUpdateInputSchema = z
  .object({ ...todoCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une todo. */
export type TodoUpdateInput = z.infer<typeof TodoUpdateInputSchema>;
